import { generateVolumeSteps } from "../../config/audio/radioConfig";
import type { RadioConfig } from "../../config/audio/types";
import type { SceneComponent } from "../../config/interactive/types";
import { VERBS } from "../../config/interactive/verbs";
import { SCENES } from "../../config/scenes";

// Debounce function to limit the rate of function calls
export const debounce = <T extends (...args: any[]) => void>(fn: T, delay: number) => {
	let timeoutId: ReturnType<typeof setTimeout>; // https://www.typescriptlang.org/docs/handbook/utility-types.html#returntypetype
	return (...args: Parameters<T>) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => fn(...args), delay);
	};
};

export const throttle = <T extends (...args: any[]) => void>(fn: T, delay: number) => {
	let isThrottled = false;
	return (...args: Parameters<T>) => {
		if (isThrottled) return;
		fn(...args);
		isThrottled = true;
		setTimeout(() => (isThrottled = false), delay);
	};
};

/**
 * Determines whether a scene component is enabled.
 *
 * @param component - The scene component to check.
 * @returns True if the component is enabled, otherwise false.
 */
export const isComponentEnabled = (component: SceneComponent): boolean => {
	if (component.disabled) {
		return false;
	}
	return component.verbs
		.filter((verb) => verb.name === VERBS.NAVIGATE)
		.every((verb) => isSceneEnabled(verb.data.url));
};

export const isComponentDisabled = (component: SceneComponent): boolean =>
	!isComponentEnabled(component);

/**
 * Determines whether a scene is enabled.
 *
 * @param scenePath - The path of the scene to check.
 * @returns True if the scene exists and is enabled; false if disabled or not found.
 */
export const isSceneEnabled = (scenePath: string): boolean => {
	const scene = Object.values(SCENES).find((s: { path: string }) => s.path === scenePath);
	return !scene?.disabled;
};

/**
 * Calculates the next radio volume step and gain value based on current level and direction.
 *
 * @param currentLevel - The current volume step level.
 * @param direction - The direction to adjust volume ("up" or "down").
 * @param volumeConfig - Optional volume configuration to use custom steps.
 * @returns [newStepLevel, gainValue]
 */
export const radioVolumeGetNextStep = (
	currentLevel: number,
	direction: "up" | "down",
	volumeConfig: RadioConfig,
): [number, number] => {
	const volumeSteps = generateVolumeSteps(volumeConfig);
	const maxStep = volumeSteps.length - 1;

	const newLevel =
		direction === "up" ? Math.min(currentLevel + 1, maxStep) : Math.max(currentLevel - 1, 0);

	return [newLevel, volumeSteps[newLevel]];
};

export const toPercent = (value: number): number => {
	return Math.round(value * 100);
};
